import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Component for changing cart item variants (color/size) directly in the cart.
 * Uses custom dropdowns with color swatches.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [colorDropdown] - The color dropdown container.
 * @property {HTMLElement} [colorTrigger] - The color dropdown trigger button.
 * @property {HTMLElement} [colorValue] - The color value display.
 * @property {HTMLElement} [colorMenu] - The color dropdown menu.
 * @property {HTMLElement} [sizeDropdown] - The size dropdown container.
 * @property {HTMLElement} [sizeTrigger] - The size dropdown trigger button.
 * @property {HTMLElement} [sizeValue] - The size value display.
 * @property {HTMLElement} [sizeMenu] - The size dropdown menu.
 *
 * @extends {Component<Refs>}
 */
class CartItemVariantSelector extends Component {
  /** @type {Array<{id: number, available: boolean, options: string[]}>} */
  #variants = [];

  /** @type {string[]} */
  #currentOptions = [];

  /** @type {Record<string, string>} */
  #colorMap = {};

  /** @type {AbortController|null} */
  #outsideClickController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parseVariantData();
    this.#parseCurrentOptions();
    this.#parseColorMap();
    this.#applySwatchColors();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#outsideClickController?.abort();
  }

  /**
   * Parses the variant data from the embedded JSON script.
   */
  #parseVariantData() {
    const dataScript = this.querySelector('[data-variant-data]');
    if (dataScript?.textContent) {
      try {
        this.#variants = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant data', e);
      }
    }
  }

  /**
   * Parses the current options from the embedded JSON script.
   */
  #parseCurrentOptions() {
    const optionsScript = this.querySelector('[data-current-options]');
    if (optionsScript?.textContent) {
      try {
        this.#currentOptions = JSON.parse(optionsScript.textContent);
      } catch (e) {
        console.error('Failed to parse current options', e);
      }
    }
  }

  /**
   * Parses the color map from the embedded JSON script.
   */
  #parseColorMap() {
    const colorMapScript = this.querySelector('[data-color-map]');
    if (colorMapScript?.textContent) {
      try {
        this.#colorMap = JSON.parse(colorMapScript.textContent);
      } catch (e) {
        console.error('Failed to parse color map', e);
      }
    }
  }

  /**
   * Applies color hex values to swatches based on color map.
   */
  #applySwatchColors() {
    const swatches = this.querySelectorAll('.cart-variant-dropdown__swatch');
    swatches.forEach((swatch) => {
      const colorHandle = swatch.getAttribute('data-color');
      if (colorHandle && this.#colorMap[colorHandle]) {
        const colorValue = this.#colorMap[colorHandle];
        if (colorValue.startsWith('linear-gradient')) {
          swatch.style.background = colorValue;
        } else {
          swatch.style.backgroundColor = colorValue;
        }
      }
    });
  }

  /**
   * Toggles dropdown open/close state.
   * @param {object} data - The event data.
   * @param {Event} event - The click event.
   */
  toggleDropdown(data, event) {
    event.preventDefault();
    event.stopPropagation();

    const dropdownType = data.dropdown; // 'color' or 'size'
    const dropdown = this.refs[`${dropdownType}Dropdown`];
    const trigger = this.refs[`${dropdownType}Trigger`];

    if (!dropdown) return;

    const isOpen = dropdown.hasAttribute('open');

    // Close all dropdowns first
    this.#closeAllDropdowns();

    if (!isOpen) {
      dropdown.setAttribute('open', '');
      trigger?.setAttribute('aria-expanded', 'true');
      this.#setupOutsideClickListener();
    }
  }

  /**
   * Selects an option from the dropdown.
   * @param {object} data - The event data containing dropdown type and value.
   * @param {Event} event - The click event.
   */
  selectOption(data, event) {
    event.preventDefault();
    event.stopPropagation();

    const dropdownType = data.dropdown; // 'color' or 'size'
    const newValue = decodeURIComponent(data.value);

    const dropdown = this.refs[`${dropdownType}Dropdown`];
    const trigger = this.refs[`${dropdownType}Trigger`];
    const valueDisplay = this.refs[`${dropdownType}Value`];

    if (!dropdown || !trigger) return;

    // Get option position from trigger
    const optionPosition = parseInt(trigger.dataset.optionPosition || '1', 10);

    // Update display
    if (valueDisplay) {
      valueDisplay.textContent = newValue;
    }

    // Update swatch if color
    if (dropdownType === 'color') {
      const triggerSwatch = trigger.querySelector('.cart-variant-dropdown__swatch');
      if (triggerSwatch) {
        const colorHandle = newValue.toLowerCase().replace(/\s+/g, '-');
        triggerSwatch.setAttribute('data-color', colorHandle);
        if (this.#colorMap[colorHandle]) {
          const colorValue = this.#colorMap[colorHandle];
          if (colorValue.startsWith('linear-gradient')) {
            triggerSwatch.style.background = colorValue;
          } else {
            triggerSwatch.style.backgroundColor = colorValue;
          }
        }
      }
    }

    // Update selected state in menu
    const menu = this.refs[`${dropdownType}Menu`];
    if (menu) {
      const items = menu.querySelectorAll('.cart-variant-dropdown__item');
      items.forEach((item) => {
        const itemValue = item.getAttribute('data-value');
        if (itemValue === newValue) {
          item.classList.add('cart-variant-dropdown__item--selected');
          item.setAttribute('aria-selected', 'true');
        } else {
          item.classList.remove('cart-variant-dropdown__item--selected');
          item.removeAttribute('aria-selected');
        }
      });
    }

    // Close dropdown
    this.#closeAllDropdowns();

    // Process variant change
    this.#onOptionChange(optionPosition, newValue);
  }

  /**
   * Closes all dropdowns.
   */
  #closeAllDropdowns() {
    const dropdowns = this.querySelectorAll('[data-dropdown]');
    dropdowns.forEach((dropdown) => {
      dropdown.removeAttribute('open');
    });

    const triggers = this.querySelectorAll('.cart-variant-dropdown__trigger');
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.#outsideClickController?.abort();
    this.#outsideClickController = null;
  }

  /**
   * Sets up listener to close dropdown when clicking outside.
   */
  #setupOutsideClickListener() {
    this.#outsideClickController?.abort();
    this.#outsideClickController = new AbortController();

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target instanceof Node && !this.contains(target)) {
          this.#closeAllDropdowns();
        }
      },
      { signal: this.#outsideClickController.signal }
    );
  }

  /**
   * Handles option change.
   * @param {number} optionPosition - The option position (1-based).
   * @param {string} newValue - The new option value.
   */
  #onOptionChange(optionPosition, newValue) {
    // Build new options array based on the CURRENT state
    const newOptions = [...this.#currentOptions];
    newOptions[optionPosition - 1] = newValue;

    // Find matching variant
    const targetVariant = this.#variants.find((variant) => {
      return variant.options.every((opt, i) => opt === newOptions[i]);
    });

    if (!targetVariant) {
      console.warn('No variant found for options:', newOptions);
      return;
    }

    if (!targetVariant.available) {
      console.warn('Selected variant is not available:', newOptions);
      return;
    }

    // CRITICAL: sync internal state BEFORE the async cart update.
    // morphSection preserves this custom-element instance after re-render,
    // so connectedCallback is NOT re-run. Without this, #currentOptions stays
    // stale and the next change reverts the other dropdown to its original value.
    this.#currentOptions = newOptions;
    this.dataset.currentVariantId = String(targetVariant.id);

    this.#updateCartLineVariant(targetVariant.id);
  }

  /**
   * Updates the cart line with a new variant.
   * @param {number} newVariantId - The new variant ID.
   */
  async #updateCartLineVariant(newVariantId) {
    let lineIndex = parseInt(this.dataset.lineIndex || '1', 10);
    const currentVariantId = this.dataset.currentVariantId;

    // Shopify cart lines are 1-based, but item.index is 0-based
    // So we need to add 1 to the index
    if (lineIndex === 0) {
      console.warn('Line index was 0, adding 1. Dataset value:', this.dataset.lineIndex);
      lineIndex = 1;
    } else {
      // item.index is 0-based, so add 1 for the API
      lineIndex = lineIndex + 1;
    }

    if (newVariantId.toString() === currentVariantId) return;

    console.log('🔄 Changing cart variant:', {
      lineIndex,
      currentVariantId,
      newVariantId,
    });

    this.classList.add('updating');

    // Get quantity from the cart item row (table) or drawer container
    const cartItemRow = this.closest('tr, [data-cart-item], .cart-item');
    const quantityInput = cartItemRow?.querySelector('input[type="number"], input[name="updates[]"]');
    const quantity = quantityInput ? parseInt(/** @type {HTMLInputElement} */ (quantityInput).value, 10) || 1 : 1;

    // Get all cart-items-components to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sectionsParam = Array.from(sectionsToUpdate).join(',');

    try {
      const maxRetries = 3;

      // Helper function for fetch with retry
      const fetchWithRetry = async (url, options) => {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          const response = await fetch(url, options);
          if (response.status === 429) {
            if (attempt < maxRetries) {
              const waitTime = Math.pow(2, attempt + 1) * 500;
              console.warn(`Rate limited (429). Retrying in ${waitTime}ms... (attempt ${attempt + 1}/${maxRetries})`);
              await new Promise((resolve) => setTimeout(resolve, waitTime));
              continue;
            }
            throw new Error('Max retries exceeded due to rate limiting');
          }
          return response;
        }
      };

      // Step 1: Remove the old variant from cart
      console.log('🗑️ Removing old variant, line:', lineIndex);
      const removeBody = JSON.stringify({
        line: lineIndex,
        quantity: 0,
        sections: sectionsParam,
        sections_url: window.location.pathname,
      });

      const removeResponse = await fetchWithRetry(Theme.routes.cart_change_url, fetchConfig('json', { body: removeBody }));
      const removeResult = await removeResponse.json();

      console.log('✅ Old variant removed:', removeResult);

      if (removeResult.errors) {
        console.error('Remove error:', removeResult.errors);
        this.classList.remove('updating');
        return;
      }

      // Step 2: Add the new variant to cart
      console.log('➕ Adding new variant:', newVariantId, 'quantity:', quantity);
      const addFormData = new FormData();
      addFormData.append('id', newVariantId.toString());
      addFormData.append('quantity', quantity.toString());
      addFormData.append('sections', sectionsParam);
      addFormData.append('sections_url', window.location.pathname);

      const addResponse = await fetchWithRetry(Theme.routes.cart_add_url, {
        method: 'POST',
        body: addFormData,
      });

      const result = await addResponse.json();

      console.log('📥 New variant added:', result);

      if (result.errors) {
        console.error('Cart update error:', result.errors);
        return;
      }

      // Calculate new item count
      const newItemCount = result.item_count || 0;

      // Dispatch cart update event
      this.dispatchEvent(
        new CartUpdateEvent({}, 'cart-item-variant-selector', {
          source: 'variant-change',
          itemCount: newItemCount,
          sections: result.sections,
        })
      );

      // Morph each section
      for (const sectionId of sectionsToUpdate) {
        if (result.sections[sectionId]) {
          morphSection(sectionId, result.sections[sectionId]);
        }
      }

      // Re-sync internal state from the freshly rendered DOM.
      // If morph preserved this instance, the embedded JSON scripts now hold
      // the new server-authoritative values — re-parse them so any next
      // interaction starts from the truth, not an in-memory snapshot.
      this.#parseVariantData();
      this.#parseCurrentOptions();
    } catch (error) {
      console.error('Failed to update cart variant:', error);
    } finally {
      this.classList.remove('updating');
    }
  }
}

if (!customElements.get('cart-item-variant-selector')) {
  customElements.define('cart-item-variant-selector', CartItemVariantSelector);
}